// ============================================
// Moon Shaders
// Fragment and vertex shaders for Moon rendering
// ============================================

import { earthShaderFunctions } from './common';

/**
 * Moon surface vertex shader
 */
export const moonVertexShader = `
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vPosition;

    void main() {
        vUv = uv;
        vNormal = normalize(normalMatrix * normal);
        vPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

/**
 * Moon surface fragment shader - gray surface with craters
 */
export const moonFragmentShader = `
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vPosition;
    uniform float time;
    uniform vec3 sunDirection;

    ${earthShaderFunctions}

    // Crater function
    float crater(vec2 uv, vec2 center, float radius, float depth) {
        float dist = length(uv - center);
        float rimWidth = radius * 0.15;

        // Crater interior (darker)
        float interior = smoothstep(radius, radius * 0.3, dist) * depth;

        // Rim (brighter)
        float rim = smoothstep(radius + rimWidth, radius, dist) *
                    smoothstep(radius - rimWidth * 0.5, radius, dist) * 0.15;

        return interior - rim;
    }

    void main() {
        vec2 uv = vUv;

        // Fixed view direction
        vec3 viewDir = vec3(0.0, 0.0, 1.0);
        vec3 normal = normalize(vNormal);

        // Fresnel coefficient
        float fresnel = 1.0 - max(0.0, dot(normal, viewDir));
        float fresnelPow = pow(fresnel, 1.5);

        // Sun light calculation
        float sunLight = max(0.0, dot(normal, sunDirection));
        float ambient = 0.15;
        float diffuse = sunLight * 0.85;
        float lighting = ambient + diffuse;

        // ===== Moon base color (gray highlands) =====
        vec3 highland = vec3(0.72, 0.70, 0.68);  // Light gray
        vec3 maria = vec3(0.35, 0.33, 0.32);     // Dark gray (mare)

        // Surface variation with noise
        float surfaceNoise = fbm(uv * 20.0, 5);
        float surfaceDetail = fbm(uv * 50.0, 4) * 0.3;

        // Create lunar maria (dark patches)
        float mariaPattern = 0.0;
        // Mare Tranquillitatis area
        mariaPattern += smoothstep(0.15, 0.0, length(uv - vec2(0.55, 0.45))) * 0.7;
        // Mare Serenitatis area
        mariaPattern += smoothstep(0.12, 0.0, length(uv - vec2(0.58, 0.58))) * 0.6;
        // Mare Imbrium area
        mariaPattern += smoothstep(0.14, 0.0, length(uv - vec2(0.40, 0.55))) * 0.65;
        // Oceanus Procellarum area
        mariaPattern += smoothstep(0.18, 0.0, length(uv - vec2(0.30, 0.45))) * 0.55;

        // Mix highland and maria
        vec3 baseColor = mix(highland, maria, clamp(mariaPattern, 0.0, 1.0));

        // Add surface variation
        baseColor += (surfaceNoise - 0.5) * 0.15;
        baseColor += surfaceDetail * 0.08;

        // ===== Craters =====
        float craterDarkening = 0.0;

        // Large craters
        craterDarkening += crater(uv, vec2(0.70, 0.65), 0.06, 0.18);  // Tycho-like
        craterDarkening += crater(uv, vec2(0.25, 0.72), 0.05, 0.15);  // Copernicus-like
        craterDarkening += crater(uv, vec2(0.62, 0.28), 0.045, 0.14);
        craterDarkening += crater(uv, vec2(0.35, 0.35), 0.04, 0.12);

        // Medium craters
        craterDarkening += crater(uv, vec2(0.48, 0.78), 0.03, 0.10);
        craterDarkening += crater(uv, vec2(0.80, 0.42), 0.028, 0.09);
        craterDarkening += crater(uv, vec2(0.22, 0.55), 0.025, 0.08);
        craterDarkening += crater(uv, vec2(0.55, 0.22), 0.024, 0.08);

        // Small craters (procedural)
        for (int i = 0; i < 8; i++) {
            float fi = float(i);
            vec2 craterPos = vec2(
                fract(sin(fi * 127.1) * 43758.5453),
                fract(sin(fi * 311.7) * 43758.5453)
            );
            float craterSize = 0.008 + fract(sin(fi * 74.7) * 43758.5453) * 0.012;
            craterDarkening += crater(uv, craterPos, craterSize, 0.05);
        }

        // Apply craters
        baseColor -= craterDarkening;

        // ===== Apply lighting =====
        vec3 surfaceColor = baseColor * lighting;

        // Subtle atmospheric scattering (Earth's atmosphere reflection)
        vec3 earthShine = vec3(0.15, 0.18, 0.25);
        float nightSide = 1.0 - smoothstep(0.0, 0.15, sunLight);
        surfaceColor += earthShine * nightSide * 0.08;

        // Edge softening
        surfaceColor *= (1.0 - pow(fresnel, 3.0) * 0.2);

        gl_FragColor = vec4(surfaceColor, 1.0);
    }
`;
